import React, { useEffect, useState } from 'react'
import api from '../api/axios'
import { useAuth } from '../context/AuthContext'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { Download, RefreshCw, BarChart2, Users, Building2, CheckCircle, Clock, XCircle, TrendingUp, TrendingDown, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react'
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts'

const STATUS_COLORS: Record<string, string> = {
  'Completed': 'bg-green-100 text-green-700',
  'Unallocated': 'bg-gray-100 text-gray-600',
  'Allocated': 'bg-blue-100 text-blue-700',
  'In Process': 'bg-orange-100 text-orange-700',
  'Send to QC': 'bg-purple-100 text-purple-700',
  'Correction': 'bg-red-100 text-red-700',
  'Paused': 'bg-yellow-100 text-yellow-700',
  'Dismissed': 'bg-gray-100 text-gray-400',
  'Carryover': 'bg-teal-100 text-teal-700',
  'Query Sent': 'bg-indigo-100 text-indigo-700',
}

export default function JobReportsPage() {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [clientId, setClientId] = useState('')
  const [status, setStatus] = useState('')
  const [requestType, setRequestType] = useState('')
  const [groupBy, setGroupBy] = useState('day')
  const [clients, setClients] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState<'overview' | 'by-client' | 'by-artist' | 'jobs' | 'sla'>('overview')
  const [slaData, setSlaData] = useState<any>(null)
  const [slaLoading, setSlaLoading] = useState(false)
  const [jobSearch, setJobSearch] = useState('')
  const [jobPage, setJobPage] = useState(1)

  useEffect(() => {
    api.get('/clients').then(res => setClients(res.data)).catch(() => {})
    fetchReport()
  }, [])

  const fetchReport = async () => {
    setLoading(true)
    try {
      let url = `/reports/jobs?groupBy=${groupBy}`
      if (dateFrom) url += `&dateFrom=${dateFrom}`
      if (dateTo) url += `&dateTo=${dateTo}`
      if (clientId) url += `&clientId=${clientId}`
      if (status) url += `&status=${encodeURIComponent(status)}`
      if (requestType) url += `&requestType=${requestType}`
      const res = await api.get(url)
      setData(res.data)
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  const toLocalDateStr = (d: Date) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  const handleThisMonth = () => {
    const now = new Date()
    const first = new Date(now.getFullYear(), now.getMonth(), 1)
    setDateFrom(toLocalDateStr(first))
    setDateTo(toLocalDateStr(now))
  }

  const handleLastMonth = () => {
    const now = new Date()
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const last = new Date(now.getFullYear(), now.getMonth(), 0)
    setDateFrom(toLocalDateStr(first))
    setDateTo(toLocalDateStr(last))
  }

  const { hasPermission } = useAuth()
  const canExport = hasPermission('export_reports')
  const canViewDashboard = hasPermission('view_reports_dashboard')
  const [dashData, setDashData] = useState<any>(null)
  const [dashLoading, setDashLoading] = useState(false)
  const [dashOpen, setDashOpen] = useState(true)
  const [dayChartClient, setDayChartClient] = useState('all')
  const [volumeChartClient, setVolumeChartClient] = useState('all')
  const [turnaroundChartClient, setTurnaroundChartClient] = useState('all')

  useEffect(() => {
    if (!canViewDashboard) return
    setDashLoading(true)
    api.get('/reports/dashboard').then(res => setDashData(res.data)).catch(() => {}).finally(() => setDashLoading(false))
  }, [canViewDashboard])
  useEffect(() => {
    if (activeTab !== 'sla') return
    if (slaData) return
    setSlaLoading(true)
    api.get('/reports/sla').then(res => setSlaData(res.data)).catch(() => {}).finally(() => setSlaLoading(false))
  }, [activeTab])

  const handleExportPDF = async () => {
    if (!data) return
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
      const pageWidth = doc.internal.pageSize.getWidth()

      // Load logo
      const logoUrl = '/New Inkcredible Logo.webp'
      const { imgData, imgAspect } = await new Promise<{ imgData: string, imgAspect: number }>((resolve) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext('2d')!
          ctx.drawImage(img, 0, 0)
          resolve({ imgData: canvas.toDataURL('image/png'), imgAspect: img.height / img.width })
        }
        img.src = logoUrl
      })

      // Header background
      doc.setFillColor(7, 24, 41)
      doc.rect(0, 0, pageWidth, 32, 'F')

      // Logo
      const logoW = 55
      const logoH = logoW * imgAspect
      doc.addImage(imgData, 'PNG', 6, 4, logoW, logoH)

      // Title
      doc.setTextColor(200, 215, 230)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.text('Job Reports', 8, 27)

      // Date range
      const dateStr = dateFrom && dateTo
        ? `${dateFrom} to ${dateTo}`
        : dateFrom ? `From ${dateFrom}` : dateTo ? `To ${dateTo}` : 'All time'
      doc.setFontSize(8)
      doc.setTextColor(150, 170, 190)
      doc.text(dateStr, pageWidth - 10, 27, { align: 'right' })

      // Summary row
      doc.setFillColor(15, 40, 70)
      doc.rect(0, 32, pageWidth, 12, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(8)
      doc.setFont('helvetica', 'bold')
      const summaryItems = [
        `Total: ${data.summary.total}`,
        `Completed: ${data.summary.completed}`,
        `In Progress: ${data.summary.inProgress}`,
        `Fresh: ${data.summary.fresh}`,
        `Revision: ${data.summary.revision}`,
        `Dismissed: ${data.summary.dismissed}`
      ]
      const colW = pageWidth / summaryItems.length
      summaryItems.forEach((item, i) => {
        doc.text(item, colW * i + colW / 2, 39.5, { align: 'center' })
      })

      // Jobs table
      const filtered = data.jobs.filter((j: any) =>
        !jobSearch || j.jobCode.toLowerCase().includes(jobSearch.toLowerCase()) ||
        j.displayTitle?.toLowerCase().includes(jobSearch.toLowerCase()) ||
        j.client?.toLowerCase().includes(jobSearch.toLowerCase())
      )

      autoTable(doc, {
        startY: 46,
        head: [['Job Code', 'Title', 'Client', 'Type', 'Status', 'Time Taken', 'Date']],
        body: filtered.map((j: any) => [
          j.jobCode,
          j.displayTitle || '—',
          j.client || '—',
          j.requestType || '—',
          j.status,
          j.timeTaken > 0 ? (j.timeTaken >= 60 ? `${Math.floor(j.timeTaken / 60)}h ${Math.round(j.timeTaken % 60)}m` : `${Math.round(j.timeTaken)}m`) : '—',
          new Date(j.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
        ]),
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [249, 115, 22], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 28 },
          1: { cellWidth: 60 },
          2: { cellWidth: 35 },
          3: { cellWidth: 20 },
          4: { cellWidth: 25 },
          5: { cellWidth: 20 },
          6: { cellWidth: 25 }
        }
      })

      const dateFileStr = new Date().toISOString().split('T')[0]
      doc.save(`job-report-${dateFileStr}.pdf`)
    } catch (e) {
      console.error('PDF export failed', e)
    }
  }

  const handleExportXLSX = () => {
    if (!data) return
    const wb = XLSX.utils.book_new()
    const summaryData = [
      ['Metric', 'Value'],
      ['Total Jobs', data.summary.total],
      ['Completed', data.summary.completed],
      ['In Progress', data.summary.inProgress],
      ['Dismissed', data.summary.dismissed],
      ['Fresh', data.summary.fresh],
      ['Revision', data.summary.revision],
    ]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), 'Summary')
    const jobsData = [
      ['Job Code', 'Title', 'Client', 'Type', 'Status', 'Artist', 'QC', 'Time Taken (hrs)', 'Turnaround', 'Date'],
      ...data.jobs.map((j: any) => [
        j.jobCode, j.displayTitle, j.client, j.requestType,
        j.status, j.artist, j.qc,
        j.timeTaken > 0 ? (j.timeTaken / 60).toFixed(2) : '—',
        j.turnaround != null ? (j.turnaround < 24 ? `${j.turnaround}h` : `${Math.round(j.turnaround / 24 * 10) / 10}d`) : '—',
        new Date(j.createdAt).toLocaleDateString('en-IN')
      ])
    ]
    const jobsWs = XLSX.utils.aoa_to_sheet(jobsData)
    jobsWs['!cols'] = jobsData[0].map((h: string) => ({ wch: Math.max(h.length, 12) }))
    XLSX.utils.book_append_sheet(wb, jobsWs, 'Jobs')
    const clientData = [
      ['Client', 'Total Jobs', 'Completed', 'Fresh', 'Revision', 'Correction', 'Avg Turnaround'],
      ...data.byClient.map((c: any) => [
        c.name, c.total, c.completed,
        c.fresh || 0, c.revision || 0, c.correction || 0,
        c.avgTurnaround != null ? (c.avgTurnaround < 24 ? `${c.avgTurnaround}h` : `${Math.round(c.avgTurnaround / 24 * 10) / 10}d`) : '—'
      ])
    ]
    const clientWs = XLSX.utils.aoa_to_sheet(clientData)
    clientWs['!cols'] = clientData[0].map((h: string) => ({ wch: Math.max(h.length, 12) }))
    XLSX.utils.book_append_sheet(wb, clientWs, 'By Client')
    const artistData = [
      ['Artist', 'Total Jobs', 'Completed', 'Completion Rate', 'Avg Turnaround'],
      ...data.byArtist.map((a: any) => [
        a.name, a.total, a.completed,
        a.total > 0 ? `${Math.round((a.completed / a.total) * 100)}%` : '0%',
        a.avgTurnaround != null ? (a.avgTurnaround < 24 ? `${a.avgTurnaround}h` : `${Math.round(a.avgTurnaround / 24 * 10) / 10}d`) : '—'
      ])
    ]
    const artistWs = XLSX.utils.aoa_to_sheet(artistData)
    artistWs['!cols'] = artistData[0].map((h: string) => ({ wch: Math.max(h.length, 12) }))
    XLSX.utils.book_append_sheet(wb, artistWs, 'By Artist')
    const dateStr = new Date().toISOString().split('T')[0]
    XLSX.writeFile(wb, `job-report-${dateStr}.xlsx`)
  }

  const filteredJobs = data?.jobs?.filter((j: any) =>
    !jobSearch || j.jobCode.toLowerCase().includes(jobSearch.toLowerCase()) ||
    j.displayTitle?.toLowerCase().includes(jobSearch.toLowerCase()) ||
    j.client?.toLowerCase().includes(jobSearch.toLowerCase())
  ) || []

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy-900">Job Reports</h1>
          <p className="text-gray-400 mt-1 text-sm">Analyse job performance and activity</p>
        </div>
        <div className="flex items-center gap-2">
        </div>
      </div>

      {canViewDashboard && (
        <div className="card overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between cursor-pointer"
            onClick={() => setDashOpen(v => !v)}>
            <div className="flex items-center gap-2">
              <BarChart2 size={16} className="text-orange-500" />
              <p className="font-display font-semibold text-navy-900 dark:text-white">Reports Dashboard</p>
              <span className="text-xs text-gray-400">— this month</span>
            </div>
            {dashOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </div>

          {dashOpen && (
            <div className="p-5 space-y-6">
              {dashLoading ? (
                <div className="py-8 text-center text-gray-400 text-sm">Loading dashboard...</div>
              ) : dashData ? (
                <>
                  {/* KPI Row */}
                  <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
                    {[
                      { label: 'Jobs This Month', value: dashData.kpis.totalThisMonth, sub: `${dashData.kpis.volumeChange !== null ? (dashData.kpis.volumeChange >= 0 ? '+' : '') + dashData.kpis.volumeChange + '% vs last month' : ''}`, up: dashData.kpis.volumeChange >= 0 },
                      { label: `Last Month (1–${new Date().getDate()})`, value: dashData.kpis.lastMonthSameDay ?? '—', sub: dashData.kpis.sameDayChange !== null ? `${dashData.kpis.sameDayChange >= 0 ? '+' : ''}${dashData.kpis.sameDayChange}% vs same period` : 'no prior data', up: (dashData.kpis.sameDayChange ?? 0) >= 0 },
                      { label: 'Completed', value: dashData.kpis.completedThisMonth, sub: `of ${dashData.kpis.totalThisMonth} jobs`, up: true },
                      { label: 'Avg Turnaround', value: dashData.kpis.avgTurnaround + 'h', sub: 'completed jobs', up: dashData.kpis.avgTurnaround < 48 },
                      { label: 'Correction Rate', value: dashData.kpis.correctionRate + '%', sub: 'errors + corrections', up: dashData.kpis.correctionRate < 10 },
                      { label: 'Avg Jobs / Day', value: dashData.kpis.avgJobsPerDay ?? '—', sub: dashData.kpis.avgHoursPerDay ? `~${dashData.kpis.avgHoursPerDay}h avg per day` : 'working days this month', up: true },
                    ].map(k => (
                      <div key={k.label} className="card p-4 transition-all duration-200 hover:shadow-[0_4px_16px_rgba(249,115,22,0.15)] hover:border-orange-200 group">
                        <div className="flex items-baseline gap-2">
                          <p className="text-3xl font-display font-bold text-[#123D68] dark:text-white leading-none group-hover:text-orange-500 transition-colors">{k.value}</p>
                        </div>
                        <p className="text-xs font-light text-gray-400 mt-1">{k.label}</p>
                        {k.sub && (
                          <p className={`text-xs mt-0.5 font-medium flex items-center gap-0.5 ${k.up ? 'text-green-500' : 'text-red-400'}`}>
                            {k.up ? <TrendingUp size={10} /> : <TrendingDown size={10} />} {k.sub}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Row 1 — Volume Trend + Day of Week */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="card p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-semibold text-navy-900 dark:text-white text-sm">Volume Trend <span className="text-xs font-normal text-gray-400">— last 12 months</span></p>
                        <select className="text-[10px] border border-gray-200 dark:border-[#1e3450] rounded-lg px-2 py-1 text-gray-600 dark:text-gray-300 bg-white dark:bg-[#162840] max-w-[130px]"
                          value={volumeChartClient} onChange={e => setVolumeChartClient(e.target.value)}>
                          <option value="all">All Clients</option>
                          {Object.keys(dashData.volumeTrendPerClient || {}).map(cid => {
                            const name = dashData.timingBenchmarks?.find((t: any) => t.clientId === cid)?.name || cid
                            return <option key={cid} value={cid}>{name}</option>
                          })}
                        </select>
                      </div>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={volumeChartClient === 'all' ? dashData.volumeTrend : (dashData.volumeTrendPerClient?.[volumeChartClient] || dashData.volumeTrend)} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                          <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                          <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="fresh" stackId="a" fill="#f97316" name="Fresh" />
                          <Bar dataKey="revision" stackId="a" fill="#3b82f6" name="Revision" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="card p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-semibold text-navy-900 dark:text-white text-sm">Avg Jobs by Day <span className="text-xs font-normal text-gray-400">— last 30 days</span></p>
                        <select className="text-[10px] border border-gray-200 dark:border-[#1e3450] rounded-lg px-2 py-1 text-gray-600 dark:text-gray-300 bg-white dark:bg-[#162840] max-w-[130px]"
                          value={dayChartClient} onChange={e => setDayChartClient(e.target.value)}>
                          <option value="all">All Clients</option>
                          {Object.keys(dashData.volumeByDayPerClient || {}).map(cid => {
                            const name = dashData.timingBenchmarks?.find((t: any) => t.clientId === cid)?.name || cid
                            return <option key={cid} value={cid}>{name}</option>
                          })}
                        </select>
                      </div>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={dayChartClient === 'all' ? dashData.volumeByDay : (dashData.volumeByDayPerClient?.[dayChartClient] || dashData.volumeByDay)} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                          <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                          <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                          <Bar dataKey="count" fill="#10b981" name="Avg Jobs" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Row 2 — Turnaround Trend + Top Artist per Client */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="card p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-semibold text-navy-900 dark:text-white text-sm">Turnaround Time Trend <span className="text-xs font-normal text-gray-400">(avg hours)</span></p>
                        <select className="text-[10px] border border-gray-200 dark:border-[#1e3450] rounded-lg px-2 py-1 text-gray-600 dark:text-gray-300 bg-white dark:bg-[#162840] max-w-[130px]"
                          value={turnaroundChartClient} onChange={e => setTurnaroundChartClient(e.target.value)}>
                          <option value="all">All Clients</option>
                          {Object.keys(dashData.turnaroundTrendPerClient || {}).map(cid => {
                            const name = dashData.timingBenchmarks?.find((t: any) => t.clientId === cid)?.name || cid
                            return <option key={cid} value={cid}>{name}</option>
                          })}
                        </select>
                      </div>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={turnaroundChartClient === 'all' ? dashData.turnaroundTrend : (dashData.turnaroundTrendPerClient?.[turnaroundChartClient] || dashData.turnaroundTrend)} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                          <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                          <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} formatter={(v: any) => [`${v}h`, 'Avg Turnaround']} />
                          <Bar dataKey="avgHours" fill="#8b5cf6" name="Avg Hours" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="card p-4">
                      <p className="font-semibold text-navy-900 dark:text-white text-sm mb-3">Top Artist per Client <span className="text-xs font-normal text-gray-400">— this month</span></p>
                      <div className="space-y-2 max-h-[160px] overflow-y-auto">
                        {dashData.topArtistPerClient?.map((c: any) => (
                          <div key={c.clientName} className="flex items-center gap-2">
                            <span className="text-xs font-medium text-navy-800 dark:text-white w-20 truncate flex-shrink-0">{c.clientName}</span>
                            <span className="text-xs text-orange-500 flex-1 truncate">{c.artistName}</span>
                            <span className="text-xs text-gray-400 flex-shrink-0">{c.jobCount}/{c.totalJobs} jobs</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Client Health + Timing Benchmarks */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Client health */}
                    <div className="card p-4">
                      <p className="font-semibold text-navy-900 dark:text-white text-sm mb-3">Client Health — This Month</p>
                      <div className="space-y-2 max-h-[220px] overflow-y-auto">
                        {dashData.clientHealth.filter((c: any) => c.jobsThisMonth > 0).map((c: any) => (
                          <div key={c.name} className="flex items-center gap-2">
                            <span className="text-xs font-medium text-navy-800 dark:text-white w-20 truncate flex-shrink-0">{c.name}</span>
                            <div className="flex-1 h-1.5 bg-gray-100 dark:bg-[#1a3050] rounded-full overflow-hidden">
                              <div className="h-full bg-orange-400 rounded-full" style={{ width: `${c.completionRate}%` }} />
                            </div>
                            <span className="text-xs text-gray-400 w-16 text-right flex-shrink-0">{c.completionRate}% · {c.jobsThisMonth}j</span>
                            {c.daysLeft !== null && c.daysLeft <= 14 && (
                              <span className={`text-xs font-semibold flex-shrink-0 ${c.daysLeft <= 7 ? 'text-red-500' : 'text-yellow-500'}`}>
                                {c.daysLeft}d
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Timing benchmarks */}
                    <div className="card p-4">
                      <p className="font-semibold text-navy-900 dark:text-white text-sm mb-3">Avg Job Timing — By Client <span className="text-xs font-normal text-gray-400">— all-time</span></p>
                      <div className="space-y-2 max-h-[220px] overflow-y-auto">
                        {dashData.timingBenchmarks.map((c: any) => (
                          <div key={c.clientId} className="flex items-start gap-2">
                            <span className="text-xs font-medium text-navy-800 dark:text-white w-20 truncate flex-shrink-0">{c.name}</span>
                            <div className="flex-1 flex flex-wrap gap-1">
                              {c.fresh && <span className="text-[10px] bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded-full">Fresh: {c.fresh.avg >= 60 ? `${Math.floor(c.fresh.avg/60)}h ${c.fresh.avg%60}m` : `${c.fresh.avg}m`}</span>}
                              {c.revision && <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">Rev: {c.revision.avg >= 60 ? `${Math.floor(c.revision.avg/60)}h ${c.revision.avg%60}m` : `${c.revision.avg}m`}</span>}
                              {c.correction && <span className="text-[10px] bg-red-50 text-red-500 px-1.5 py-0.5 rounded-full">Corr: {c.correction.avg >= 60 ? `${Math.floor(c.correction.avg/60)}h ${c.correction.avg%60}m` : `${c.correction.avg}m`}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Satisfaction Trend + Per Client */}
                  {dashData.satisfactionTrend && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="card p-4">
                        <p className="font-semibold text-navy-900 dark:text-white text-sm mb-3">Satisfaction Trend <span className="text-xs font-normal text-gray-400">— last 6 months</span></p>
                        <ResponsiveContainer width="100%" height={160}>
                          <BarChart data={dashData.satisfactionTrend} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                            <YAxis domain={[0, 5]} ticks={[1,2,3,4,5]} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} formatter={(v: any) => [`${v} ★`, 'Avg Rating']} />
                            <Bar dataKey="avg" fill="#eab308" name="Avg Rating" radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="card p-4">
                        <p className="font-semibold text-navy-900 dark:text-white text-sm mb-3">Satisfaction by Client</p>
                        <div className="space-y-2 max-h-[160px] overflow-y-auto">
                          {dashData.clientSatisfaction?.map((c: any) => (
                            <div key={c.name} className="flex items-center gap-2">
                              <span className="text-xs font-medium text-navy-800 dark:text-white w-24 truncate flex-shrink-0">{c.name}</span>
                              <div className="flex-1 h-1.5 bg-gray-100 dark:bg-[#1a3050] rounded-full overflow-hidden">
                                <div className="h-full bg-yellow-400 rounded-full" style={{ width: `${(c.avg / 5) * 100}%` }} />
                              </div>
                              <span className="text-xs font-semibold text-yellow-500 w-8 text-right">{c.avg}★</span>
                              <span className="text-xs text-gray-400 w-8 text-right">({c.count})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="py-8 text-center text-gray-400 text-sm">Failed to load dashboard</div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="card p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label">From</label>
            <input type="date" className="input text-sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="label">To</label>
            <input type="date" className="input text-sm" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
          <div>
            <label className="label">Client</label>
            <select className="input text-sm" value={clientId} onChange={e => setClientId(e.target.value)}>
              <option value="">All Clients</option>
              {clients.map((c: any) => (
                <option key={c.clientId} value={c.clientId}>{c.abbreviation || c.companyName || c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Status</label>
            <select className="input text-sm" value={status} onChange={e => setStatus(e.target.value)}>
              <option value="">All Statuses</option>
              {['Unallocated','Allocated','In Process','Paused','Send to QC','Correction','Completed','Dismissed','Carryover','Query Sent'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Type</label>
            <select className="input text-sm" value={requestType} onChange={e => setRequestType(e.target.value)}>
              <option value="">All Types</option>
              <option value="fresh">Fresh</option>
              <option value="revision">Revision</option>
            </select>
          </div>
          <div>
            <label className="label">Group By</label>
            <select className="input text-sm" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={handleThisMonth} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors">This Month</button>
          <button onClick={handleLastMonth} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors">Last Month</button>
          {(dateFrom || dateTo || clientId || status || requestType) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); setClientId(''); setStatus(''); setRequestType('') }}
              className="text-xs px-3 py-1.5 rounded-lg border border-red-200 hover:bg-red-50 text-red-500 transition-colors">Clear Filters</button>
          )}
          <button onClick={fetchReport} disabled={loading}
            className="flex items-center gap-1.5 text-xs bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg font-medium transition-colors ml-auto">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> {loading ? 'Loading...' : 'Run Report'}
          </button>
          {canExport && data && (
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={handleExportPDF}
                className="flex items-center gap-1.5 text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
                <Download size={12} /> PDF
              </button>
              <button onClick={handleExportXLSX}
                className="flex items-center gap-1.5 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
                <Download size={12} /> XLSX
              </button>
            </div>
          )}
        </div>
      </div>

      {!data ? (
        <div className="card p-20 text-center text-gray-400">Run the report to see results</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: 'Total Jobs', value: data.summary.total, icon: BarChart2, color: 'text-navy-600', bg: 'bg-navy-50' },
              { label: 'In Progress', value: data.summary.inProgress, icon: Clock, color: 'text-orange-600', bg: 'bg-orange-50' },
              { label: 'Completed', value: data.summary.completed, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
              { label: 'Fresh', value: data.summary.fresh, icon: BarChart2, color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Revision', value: data.summary.revision, icon: RefreshCw, color: 'text-purple-600', bg: 'bg-purple-50' },
              { label: 'Correction', value: data.summary.correction, icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
            ].map(({ label, value }) => (
              <div key={label} className="card p-4">
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-display font-bold text-[#123D68] dark:text-white leading-none">{value}</p>
                  <p className="text-xs font-light text-gray-400 leading-none">{label}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-1 border-b border-gray-100">
            {(['overview', 'by-client', 'by-artist', 'jobs', 'sla'] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-4 py-2.5 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
                  activeTab === tab ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-navy-700'
                }`}>
                {tab === 'sla' ? 'SLA Tracking' : tab.replace('-', ' ')}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className="card p-4">
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-display font-bold text-[#123D68] dark:text-white leading-none">{data.summary.total}</p>
                  <p className="text-xs font-light text-gray-400 leading-none">Total Jobs</p>
                </div>
              </div>
              <div className="card p-4">
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-display font-bold text-[#123D68] dark:text-white leading-none">{data.summary.completed}</p>
                  <p className="text-xs font-light text-gray-400 leading-none">Completed</p>
                </div>
              </div>
              <div className="card p-4">
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-display font-bold text-[#123D68] dark:text-white leading-none">{data.summary.inProgress}</p>
                  <p className="text-xs font-light text-gray-400 leading-none">In Progress</p>
                </div>
              </div>
              <div className="card p-4">
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-display font-bold text-[#123D68] dark:text-white leading-none">
                    {data.summary.avgTurnaround != null
                      ? data.summary.avgTurnaround < 24
                        ? `${data.summary.avgTurnaround}h`
                        : `${Math.round(data.summary.avgTurnaround / 24 * 10) / 10}d`
                      : '—'}
                  </p>
                  <p className="text-xs font-light text-gray-400 leading-none">Avg Turnaround</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="card overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <p className="font-display font-semibold text-navy-900">Jobs by Status</p>
                </div>
                <div className="p-4 space-y-2">
                  {Object.entries(data.byStatus).sort(([,a]: any, [,b]: any) => b - a).map(([s, count]: any) => (
                    <div key={s} className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium w-28 text-center flex-shrink-0 ${STATUS_COLORS[s] || 'bg-gray-100 text-gray-600'}`}>{s}</span>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-orange-400 rounded-full"
                          style={{ width: `${Math.round((count / data.summary.total) * 100)}%` }} />
                      </div>
                      <span className="text-sm font-semibold text-navy-800 w-8 text-right">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <p className="font-display font-semibold text-navy-900">Jobs Over Time</p>
                </div>
                <div className="p-4 space-y-1.5 max-h-72 overflow-y-auto">
                  {data.byDate.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">No data</p>
                  ) : (
                    data.byDate.map(({ date, count }: any) => (
                      <div key={date} className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-24 flex-shrink-0 font-mono">{date}</span>
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-navy-400 rounded-full"
                            style={{ width: `${Math.round((count / Math.max(...data.byDate.map((d: any) => d.count))) * 100)}%` }} />
                        </div>
                        <span className="text-sm font-semibold text-navy-800 w-6 text-right">{count}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            </>
          )}

          {activeTab === 'by-client' && (
            <div className="card overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                <Building2 size={15} className="text-gray-400" />
                <p className="font-display font-semibold text-navy-900">Jobs by Client</p>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50/70">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Client</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Total</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Completed</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Fresh</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Revision</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Correction</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Avg Turnaround</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.byClient.map((c: any) => (
                    <tr key={c.name} className="hover:bg-orange-50/30">
                      <td className="px-6 py-3.5 font-medium text-navy-800 dark:text-white">{c.name}</td>
                      <td className="px-6 py-3.5 text-right font-mono text-navy-700 dark:text-gray-300">{c.total}</td>
                      <td className="px-6 py-3.5 text-right font-mono text-green-600">{c.completed}</td>
                      <td className="px-6 py-3.5 text-right font-mono text-orange-500 hidden sm:table-cell">{c.fresh || 0}</td>
                      <td className="px-6 py-3.5 text-right font-mono text-blue-500 hidden sm:table-cell">{c.revision || 0}</td>
                      <td className="px-6 py-3.5 text-right font-mono text-red-400 hidden sm:table-cell">{c.correction || 0}</td>
                      <td className="px-6 py-3.5 text-right hidden sm:table-cell">
                        {c.avgTurnaround != null ? (
                          <span className="text-xs font-mono text-indigo-500">
                            {c.avgTurnaround < 24 ? `${c.avgTurnaround}h` : `${Math.round(c.avgTurnaround / 24 * 10) / 10}d`}
                          </span>
                        ) : <span className="text-xs text-gray-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'by-artist' && (
            <div className="card overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                <Users size={15} className="text-gray-400" />
                <p className="font-display font-semibold text-navy-900">Jobs by Artist</p>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50/70">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Staff Name</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Total</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Completed</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Top Client</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Avg Turnaround</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.byArtist.map((a: any) => (
                    <tr key={a.name} className="hover:bg-orange-50/30">
                      <td className="px-6 py-3.5 font-medium text-navy-800 dark:text-white">{a.name}</td>
                      <td className="px-6 py-3.5 text-right font-mono text-navy-700 dark:text-gray-300">{a.total}</td>
                      <td className="px-6 py-3.5 text-right font-mono text-green-600">{a.completed}</td>
                      <td className="px-6 py-3.5 hidden sm:table-cell">
                        {a.topClient ? (
                          <span className="text-xs font-medium text-navy-700 dark:text-gray-300">
                            {a.topClient} <span className="text-gray-400">({a.topClientJobs})</span>
                          </span>
                        ) : <span className="text-xs text-gray-300">—</span>}
                      </td>
                      <td className="px-6 py-3.5 text-right hidden sm:table-cell">
                        {a.avgTurnaround != null ? (
                          <span className="text-xs font-mono text-indigo-500">
                            {a.avgTurnaround < 24 ? `${a.avgTurnaround}h` : `${Math.round(a.avgTurnaround / 24 * 10) / 10}d`}
                          </span>
                        ) : <span className="text-xs text-gray-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'jobs' && (
            <div className="card overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <p className="font-display font-semibold text-navy-900">All Jobs ({filteredJobs.length})</p>
                <input type="text" placeholder="Search jobs..." value={jobSearch}
                  onChange={e => { setJobSearch(e.target.value); setJobPage(1) }}
                  className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 w-48 focus:outline-none focus:border-orange-400" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50/70">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Job</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Client</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Type</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Status</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden lg:table-cell">Time</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredJobs.slice((jobPage - 1) * 10, jobPage * 10).map((j: any) => (
                      <tr key={j.id} className="hover:bg-orange-50/30">
                        <td className="px-6 py-3.5">
                          <p className="font-medium text-navy-800">{j.displayTitle || j.jobCode}</p>
                          <p className="text-xs font-mono text-gray-400">{j.jobCode}</p>
                        </td>
                        <td className="px-6 py-3.5 text-sm text-gray-600">{j.client}</td>
                        <td className="px-6 py-3.5 hidden sm:table-cell">
                          <span className="text-xs capitalize text-gray-500">{j.requestType}</span>
                        </td>
                        <td className="px-6 py-3.5 hidden sm:table-cell">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[j.status] || 'bg-gray-100 text-gray-600'}`}>
                            {j.status}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-xs text-gray-400 hidden lg:table-cell">
                          {j.timeTaken > 0 ? (
                            j.timeTaken >= 60
                              ? `${Math.floor(j.timeTaken / 60)}h ${Math.round(j.timeTaken % 60)}m`
                              : `${Math.round(j.timeTaken)}m`
                          ) : '—'}
                        </td>
                        <td className="px-6 py-3.5 text-xs text-gray-400">
                          {new Date(j.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredJobs.length === 0 && (
                  <div className="py-12 text-center text-gray-400 text-sm">No jobs found</div>
                )}
              </div>
              {filteredJobs.length > 10 && (() => {
                const totalPages = Math.ceil(filteredJobs.length / 10)
                return (
                  <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
                    <p className="text-xs text-gray-400">
                      Showing {(jobPage - 1) * 10 + 1}–{Math.min(jobPage * 10, filteredJobs.length)} of {filteredJobs.length} jobs
                    </p>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setJobPage(p => Math.max(1, p - 1))} disabled={jobPage === 1}
                        className="px-2.5 py-1 text-xs rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        ‹
                      </button>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <input
                          type="number" min={1} max={totalPages}
                          value={jobPage}
                          onChange={e => {
                            const v = parseInt(e.target.value)
                            if (!isNaN(v) && v >= 1 && v <= totalPages) setJobPage(v)
                          }}
                          className="w-12 text-center border border-gray-200 rounded-lg py-1 text-xs focus:outline-none focus:border-orange-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span>/ {totalPages}</span>
                      </div>
                      <button onClick={() => setJobPage(p => Math.min(totalPages, p + 1))} disabled={jobPage === totalPages}
                        className="px-2.5 py-1 text-xs rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        ›
                      </button>
                    </div>
                  </div>
                )
              })()}
            </div>
          )}
        {activeTab === 'sla' && (
            <div className="space-y-5">
              {slaLoading ? (
                <div className="py-12 text-center text-gray-400 text-sm">Loading SLA data...</div>
              ) : !slaData ? (
                <div className="py-12 text-center text-gray-400 text-sm">No SLA data available</div>
              ) : (
                <>
                  {/* Summary cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'SLA Rate', value: `${slaData.summary.slaRate}%`, sub: `Target: ${slaData.summary.slaTargetHours}h`, color: slaData.summary.slaRate >= 90 ? 'text-green-500' : slaData.summary.slaRate >= 70 ? 'text-orange-500' : 'text-red-500' },
                      { label: 'Within SLA', value: slaData.summary.withinSLA, sub: `of ${slaData.summary.total} jobs`, color: 'text-green-500' },
                      { label: 'Breached', value: slaData.summary.breached, sub: `exceeded ${slaData.summary.slaTargetHours}h`, color: slaData.summary.breached > 0 ? 'text-red-500' : 'text-gray-400' },
                      { label: 'Avg Turnaround', value: `${slaData.summary.avgHours}h`, sub: 'mean completion time', color: 'text-blue-500' },
                    ].map(k => (
                      <div key={k.label} className="bg-gray-50 dark:bg-[#162840] rounded-xl p-4">
                        <p className="text-xs text-gray-400 mb-1">{k.label}</p>
                        <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{k.sub}</p>
                      </div>
                    ))}
                  </div>

                  {/* Weekly trend */}
                  {slaData.weeklyTrend.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-navy-900 dark:text-white mb-3">Weekly SLA Trend</p>
                      <ResponsiveContainer width="100%" height={140}>
                        <LineChart data={slaData.weeklyTrend} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                          <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                          <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} domain={[0, 100]} tickFormatter={v => `${v}%`} />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} formatter={(v: any) => [`${v}%`, 'SLA Rate']} />
                          <Line type="monotone" dataKey="slaRate" stroke="#f97316" strokeWidth={2} dot={{ r: 3, fill: '#f97316' }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Client breakdown */}
                    <div>
                      <p className="text-sm font-semibold text-navy-900 dark:text-white mb-3">By Client <span className="text-xs font-normal text-gray-400">— worst first</span></p>
                      <div className="space-y-2">
                        {slaData.clientBreakdown.map((c: any) => (
                          <div key={c.name} className="flex items-center gap-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 w-24 truncate flex-shrink-0">{c.name}</p>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-[10px] text-gray-400">{c.within}/{c.total} jobs · avg {c.avgHours}h</span>
                                <span className={`text-[10px] font-bold ${c.slaRate >= 90 ? 'text-green-500' : c.slaRate >= 70 ? 'text-orange-500' : 'text-red-500'}`}>{c.slaRate}%</span>
                              </div>
                              <div className="w-full bg-gray-100 dark:bg-[#1a3050] rounded-full h-1.5">
                                <div className={`h-1.5 rounded-full ${c.slaRate >= 90 ? 'bg-green-400' : c.slaRate >= 70 ? 'bg-orange-400' : 'bg-red-400'}`}
                                  style={{ width: `${c.slaRate}%` }} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Artist breakdown */}
                    <div>
                      <p className="text-sm font-semibold text-navy-900 dark:text-white mb-3">By Artist <span className="text-xs font-normal text-gray-400">— best first</span></p>
                      <div className="space-y-2">
                        {slaData.artistBreakdown.map((a: any) => (
                          <div key={a.name} className="flex items-center gap-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 w-24 truncate flex-shrink-0">{a.name}</p>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-[10px] text-gray-400">{a.within}/{a.total} jobs · avg {a.avgHours}h</span>
                                <span className={`text-[10px] font-bold ${a.slaRate >= 90 ? 'text-green-500' : a.slaRate >= 70 ? 'text-orange-500' : 'text-red-500'}`}>{a.slaRate}%</span>
                              </div>
                              <div className="w-full bg-gray-100 dark:bg-[#1a3050] rounded-full h-1.5">
                                <div className={`h-1.5 rounded-full ${a.slaRate >= 90 ? 'bg-green-400' : a.slaRate >= 70 ? 'bg-orange-400' : 'bg-red-400'}`}
                                  style={{ width: `${a.slaRate}%` }} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
